import React, { useState, useEffect, useMemo } from 'react';
import { FiEdit2, FiFileText, FiPlus } from 'react-icons/fi';
import { getFamilies, createFamily, updateFamily } from '../../../services/api';
import '../../../styles/ModuleTable.css';

const ITEMS_OPTIONS = [10, 15, 20, 50];

function exportToExcel(headers, rows, filename) {
  let html = `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
  rows.forEach(r => { html += `<tr>${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`; });
  html += '</tbody></table>';
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportToPDF(title, headers, rows) {
  const win = window.open('', '_blank');
  const trs = rows.map((r, i) =>
    `<tr style="background:${i % 2 === 0 ? '#fff' : '#f0f7ff'}">${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`
  ).join('');
  win.document.write(`
    <html><head><title>${title}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:11px;padding:16px;color:#222}
      h2{color:#17375e;font-size:13px;margin-bottom:8px;text-transform:uppercase}
      table{border-collapse:collapse;width:100%}
      th{background:#dce6f1;color:#17375e;padding:6px 8px;text-align:left;font-size:11px;border:1px solid #9ebcdf}
      td{padding:5px 8px;border:1px solid #dde8f2;font-size:11px}
      .footer{margin-top:10px;font-size:10px;color:#888;text-align:right}
    </style></head>
    <body><h2>${title}</h2>
    <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${trs}</tbody></table>
    <div class="footer">PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })}</div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>
  `);
  win.document.close();
}

function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} of {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;&lt;</button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&gt;&gt;</button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>&gt;|</button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function FamiliaProductosMaintenance({ businessId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);

  const [filterName, setFilterName] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  useEffect(() => { load(); }, [businessId]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await getFamilies(businessId);
      setData(res.data.data || []);
    } catch (e) {
      alert('Error al cargar familias: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return data.filter(r => {
      const n = (r.name || '').toUpperCase();
      if (filterName && !n.includes(filterName.toUpperCase())) return false;
      if (search && !n.includes(search.toUpperCase())) return false;
      return true;
    });
  }, [data, filterName, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew = () => { setFormName(''); setEditId(null); setModal(true); };

  const openEdit = (row) => {
    setFormName(row.name || '');
    setEditId(row.id);
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await updateFamily(editId, businessId, { name: formName.trim() });
      } else {
        await createFamily(businessId, { name: formName.trim() });
      }
      setModal(false);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const excelData = () => {
    exportToExcel(['PRESENTACION'], filtered.map(r => [r.name]), 'FamiliaProductos');
  };
  const pdfData = () => {
    exportToPDF('CLASE O FAMILIA', ['PRESENTACION'], filtered.map(r => [r.name]));
  };

  const ExportBar = () => (
    <div className="mt-export-bar">
      <button className="btn-excel" onClick={excelData} title="Exportar Excel">
        <span style={{ fontSize: 14 }}>🐦</span> Excel
      </button>
      <button className="btn-pdf" onClick={pdfData} title="Exportar PDF">
        <FiFileText size={12} /> PDF
      </button>
      <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
    </div>
  );

  return (
    <div className="mt-container">
      {/* Toolbar */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <span className="mt-title">CLASE O FAMILIA</span>
          <button className="btn-nuevo" onClick={openNew}>
            <FiPlus size={12} /> Nuevo
          </button>
        </div>
        <div className="mt-search-global">
          <span>Busqueda Global</span>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <ExportBar />

      {/* Tabla */}
      <div className="mt-table-wrap">
        {loading ? (
          <div className="mt-loading">Cargando...</div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr className="mt-head-row">
                <th>Presentacion</th>
                <th style={{ width: 30 }}></th>
              </tr>
              <tr className="mt-filter-row">
                <th>
                  <input value={filterName} onChange={e => { setFilterName(e.target.value); setPage(1); }} />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr className="mt-empty"><td colSpan={2}>Sin registros</td></tr>
              ) : paged.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                  <td style={{ fontWeight: 500 }}>{row.name}</td>
                  <td className="td-actions">
                    <button className="btn-row-edit" onClick={() => openEdit(row)} title="Editar">
                      <FiEdit2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ExportBar />

      {/* Modal */}
      {modal && (
        <div className="mt-modal-overlay">
          <div className="mt-modal" style={{ maxWidth: 400 }}>
            <div className="mt-modal-header">
              <span>{editId ? 'Editar' : 'Nueva'} Clase o Familia</span>
              <button className="btn-modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="mt-modal-body">
                <div className="mt-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="mt-field">
                    <label>Presentacion <span className="req">*</span></label>
                    <input
                      value={formName}
                      onChange={e => setFormName(e.target.value.toUpperCase())}
                      required
                      autoFocus
                    />
                  </div>
                </div>
              </div>
              <div className="mt-modal-footer">
                <button type="submit" className="btn-modificar" disabled={saving}>
                  {saving ? 'Guardando...' : 'Modificar'}
                </button>
                <button type="button" className="btn-salir" onClick={() => setModal(false)}>
                  ✕ Salir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FamiliaProductosMaintenance;
